import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  computed,
  input,
  ChangeDetectorRef,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AsyncPipe, CommonModule } from '@angular/common';
import { Message } from '@eda/shared/models/message.model';
import { SuggestionChip } from '../suggestion-chip/suggestion-chip';
import { TimeAgoPipe } from '@eda/shared/pipes/time-ago-pipe-pipe';
import { marked } from 'marked';
import { Reaction } from '@etninfinite/aiops';
import { FileContentService } from '@eda/core/services/file-content-service';
import {
  Observable,
  Subject,
  catchError,
  firstValueFrom,
  map,
  of,
  takeUntil,
  shareReplay,
  switchMap,
} from 'rxjs';
import { openNewWindowRender } from '@eda/shared/utils/message-util';
import { EdaApiService } from '@eda/core/services/eda-api-service';
import { PreserveImagesDirective } from './directives/preserve-images-directive';
import { TranslatePipe } from '@ngx-translate/core';

type ReactionPayload = {
  isLiked: boolean | string;
  feedbackText?: string;
};

@Component({
  selector: 'aiops-message-block',
  imports: [
    CommonModule,
    SuggestionChip,
    TimeAgoPipe,
    Reaction,
    AsyncPipe,
    PreserveImagesDirective,
    TranslatePipe,
  ],
  templateUrl: './message-block.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageBlock implements OnInit, OnDestroy {
  message = input.required<Message>();
  isStreamingMessage = computed(() => this.message()?.isStreaming ?? false);
  messageText = computed(() => this.message()?.text || '');

  private edaApiService = inject(EdaApiService);

  private readonly cdr = inject(ChangeDetectorRef);

  private readonly suggestionRegex = /\\?<\\?<\s*([^>]+?)\s*\\?>\\?>/;

  private getSuggestionRegex(): RegExp {
    return new RegExp(this.suggestionRegex.source, 'g');
  }
  private destroy$ = new Subject<void>();
  private currentFeedback: boolean = false;
  suggestions = computed(() => {
    if (this.isStreamingMessage()) return [];

    const text = this.message()?.text ?? '';
    const matches: string[] = [];
    const regex = this.getSuggestionRegex();
    let match;
    while ((match = regex.exec(text)) !== null) {
      matches.push(match[1].trim());
    }
    return matches;
  });

  private fileContentService = inject(FileContentService);

  formattedMessage$: Observable<string> = toObservable(this.message).pipe(
    switchMap((msg) => this.formatMessageText(msg)),
    shareReplay(1),
  );

  /**
   * Formats message text from markdown to HTML.
   * Handles file link injection ($$fileId$$), streaming messages, and custom link rendering.
   * @param {Message | undefined} message - Message object with text and metadata.
   * @returns {Observable<string>} HTML-formatted string for display.
   */
  formatMessageText(message: Message | undefined): Observable<string> {
    const text = message?.text ?? '';

    if (message?.isOwnMessage) {
      return of(text);
    }

    const cleanText = text.replaceAll(this.getSuggestionRegex(), '');

    // a quick and dirty way to handle links to open in new window by replacing the link rednerer of marked
    const renderer = new marked.Renderer();
    renderer.link = openNewWindowRender.link;
    marked.setOptions({ renderer });

    if (message?.isStreaming) {
      return of(marked.parse(cleanText).toString());
    }

    const linkRegex = /\$\$(.*?)\$\$/g;
    const idMatch = cleanText.match(/(\$\$([a-zA-Z0-9_-]+)\$\$)/)!;

    if (!idMatch) {
      return of(marked.parse(cleanText).toString());
    }

    const documentId = idMatch[2];
    return this.fileContentService.getXLSXDownloadLink(documentId).pipe(
      map((data) => {
        const replacedLink = cleanText.replace(linkRegex, data);
        return marked.parse(replacedLink).toString();
      }),
      catchError((error) => {
        console.error('Error loading file link:', error.message);
        const errorLink = cleanText.replace(linkRegex, ``);
        return of(marked.parse(errorLink).toString());
      }),
    );
  }

  /**
   * Copies formatted message to clipboard as both HTML and plain text.
   * Supports rich text pasting in Word/Docs via ClipboardItem API, with plain text fallback.
   * @returns {Promise<void>} Resolves when content is copied.
   */
  async contentCopied(): Promise<void> {
    const formattedMessage = await firstValueFrom(this.formattedMessage$);
    const html = formattedMessage || '';
    const plainText = new DOMParser().parseFromString(html, 'text/html').body.textContent || '';

    if (window.ClipboardItem && navigator.clipboard?.write) {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([clipboardItem]);
      return;
    }

    await navigator.clipboard.writeText(plainText);
  }

  private normalizeReactionValue(value: boolean | string): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    const normalized = value.trim().toLowerCase();
    return (
      normalized === 'true' ||
      normalized === 'good' ||
      normalized === 'like' ||
      normalized === 'liked'
    );
  }

  ngOnInit(): void {
    if (this.message()?.isOwnMessage) {
      this.edaApiService
        .endSession()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          error: (err) => console.error('End session failed on init:', err),
        });
    }
  }

  /**
   * Captures thumbs up/down and starts a new feedback session before submit.
   */
  userReaction(feedback: ReactionPayload) {
    const isLiked = this.normalizeReactionValue(feedback.isLiked);
    const feedbackText = feedback.feedbackText ?? '';

    this.currentFeedback = isLiked;
    this.edaApiService
      .createFeedbackSession()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (id) => {
          this.submitFeedback(feedbackText);
        },
        error: (err) => console.error('Feedback session failed:', err),
      });
  }

  /**
   * Sends feedback payload using the active session in EdaApiService.
   */
  submitFeedback(feedbackText: string): void {
    this.edaApiService
      .submitFeedback(this.currentFeedback, feedbackText)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {},
        error: (err) => console.error('Feedback error:', err),
      });
  }

  /**
   * Ends any active feedback session, then tears down subscriptions.
   */
  ngOnDestroy(): void {
    this.edaApiService.endSession().subscribe({
      error: (err) => console.error('End session failed:', err),
    });
    this.destroy$.next();
    this.destroy$.complete();
  }
}
